import { useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useActionData, useSubmit } from 'react-router';
import Alert from 'client/components/ui/Alert/Alert';
import Modal from 'client/components/ui/Modal/Modal';
import Button from 'client/components/aria/Button/Button';
import { bookmarkTypes } from 'client/utils/constants/appStates';
import { fromCircle } from 'ol/geom/Polygon';
import GeoJSON from 'ol/format/GeoJSON';
import { MapContext } from 'os-elements';
import { MAP_PAGE } from 'client/utils/routes/routeConstants';
import { selectBufferRadiusMetres } from 'client/store/drawSlice/drawSlice';

import styles from '../BookmarkButton.module.scss';
import { CIRCLE, LINE_STRING } from 'client/utils/constants/geometryType';
import Point from 'ol/geom/Point.js';

export const BookMarkModal = ({ showSaveDialog, bookmarkType, setShowSaveDialog }) => {
    const intl = useIntl();
    const submit = useSubmit();
    const { map } = useContext(MapContext);
    const bufferRadiusMetres = useSelector(selectBufferRadiusMetres);
    const actionData = useActionData();
    const { createdBookmark, createdBookmarkError } = actionData || {};
    const bookmarkUrl = createdBookmark?.bookmarkUrl;
    const [bookmarkName, setBookmarkName] = useState('');
    const [hasBeenFocused, setHasBeenFocused] = useState(false);
    const trimmedBookmarkName = bookmarkName ? bookmarkName.trim() : '';
    const tooManyChars = trimmedBookmarkName.length > 255;
    const tooLittleChars = trimmedBookmarkName.length === 0;
    const isDisabled = tooManyChars || tooLittleChars;

    useEffect(() => {
        if (bookmarkUrl) {
            setShowSaveDialog(false);
            navigator.clipboard.writeText(bookmarkUrl);
        }
    }, [bookmarkUrl]);

    const handleCancel = () => {
        setShowSaveDialog(false);
        setHasBeenFocused(false);
    };

    const handleSave = async () => {
        const mapExtent = map.getView().calculateExtent(map.getSize());
        if (bookmarkType === bookmarkTypes.LOCATION) {
            handleCreateBookMark(bookmarkName, mapExtent);
        } else {
            let geometry = map?.getLayerById('aoi')?.getSource()?.getFeatures()[0].getGeometry();
            let buffer;
            const geomType = geometry?.getType();
            if (geomType === LINE_STRING) {
                // for lines, set a buffer
                buffer = bufferRadiusMetres;
                // if line only has 2 coordinates, convert to single point geometry
                if (geometry.flatCoordinates.length === 2) {
                    geometry = new Point(geometry.flatCoordinates);
                }
            }
            if (geomType === CIRCLE) {
                geometry = fromCircle(geometry, 60);
            }
            const reader = new GeoJSON();
            const geoJsonGeometry = reader.writeGeometry(geometry);
            handleCreateBookMark(bookmarkName, mapExtent, geoJsonGeometry, buffer);
        }
        setBookmarkName('');
    };

    const handleCreateBookMark = (name, mapExtent, geometry, bufferRadius) => {
        const formData = new FormData();
        formData.set('name', name);
        if (mapExtent) {
            formData.set('mapExtent', mapExtent);
        }
        if (geometry) {
            formData.set('geometry', geometry);
        }
        if (Number.isFinite(bufferRadius)) {
            formData.set('bufferRadiusMetres', bufferRadius);
        }
        submit(formData, {
            method: 'PUT',
            action: MAP_PAGE
        });
    };

    const saveDialogActions = <div className={styles.actions}>
        <Button
            data-testid="bookmark.button.cancel"
            variant="error"
            onPress={handleCancel}
            aria-label={intl.formatMessage({ id: 'bookmark.button.cancel.arialabel' })}
        >
            {intl.formatMessage({ id: 'bookmark.button.text.cancel' })}
        </Button>
        <Button
            data-testid="bookmark.button.save"
            variant="success"
            onPress={handleSave}
            aria-label={intl.formatMessage({ id: 'bookmark.button.save.arialabel' })}
            isDisabled={isDisabled}
        >
            {intl.formatMessage({ id: 'bookmark.button.text.save' })}
        </Button>
    </div>;

    return <Modal
        data-testid="save.bookmark.dialog"
        open={showSaveDialog}
    >
        <Alert data-testid="save.bookmark.dialog.alert" role={'alertdialog'} aria-labelledby="bookmark.dialog.title">
            <div role="document">
                <h1 className="govuk-label-wrapper" id="bookmark.dialog.title">
                    <label className="govuk-label govuk-label--l" htmlFor="bookmark.name.textfield">
                        {intl.formatMessage({ id: 'bookmark.save.title' })}
                    </label>
                </h1>
                <div className={['govuk-form-group'].join(' ')}>
                    <div className="govuk-hint">
                        {intl.formatMessage({ id: 'bookmark.save.message' })}
                    </div>
                    {createdBookmarkError &&
                    <p
                        data-testid="bookmark.save.error"
                        className="govuk-error-message"
                    >
                        <span className="govuk-visually-hidden">{intl.formatMessage({ id: 'errorLabel' })}:</span> {intl.formatMessage({ id: 'bookmark.save.error' })}
                    </p>
                    }
                    {hasBeenFocused && tooLittleChars &&
                    <p
                        data-testid="bookmark.save.tooSmall.error"
                        className="govuk-error-message"
                    >
                        <span className="govuk-visually-hidden">{intl.formatMessage({ id: 'errorLabel' })}:</span> {intl.formatMessage({ id: 'bookmark.save.tooSmall.error' })}
                    </p>
                    }
                    {hasBeenFocused && tooManyChars &&
                    <p
                        data-testid="bookmark.save.tooLong.error"
                        className="govuk-error-message"
                    >
                        <span className="govuk-visually-hidden">{intl.formatMessage({ id: 'errorLabel' })}:</span> {intl.formatMessage({ id: 'bookmark.save.tooLong.error' })}
                    </p>
                    }
                    <input
                        data-testid="bookmark.name.textfield"
                        className={['govuk-input', createdBookmarkError || tooManyChars ? 'govuk-input--error' : null].join(' ')}
                        name="bookmark-name"
                        type="text"
                        placeholder={intl.formatMessage({ id: 'bookmark.save.placeholder' })}
                        aria-label={intl.formatMessage({ id: 'bookmark.save.input.arialabel' })}
                        onChange={e => {
                            setBookmarkName(e.target.value);
                            setHasBeenFocused(true);
                        }}
                        onBlur={e => {
                            setHasBeenFocused(true);
                        }}
                    />
                    {saveDialogActions}
                </div>
            </div>
        </Alert>
    </Modal>;
};
